import {
  BeakerIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  FolderIcon,
  HomeModernIcon,
  IdentificationIcon,
  InboxArrowDownIcon,
  PhoneIcon,
  QueueListIcon,
  Squares2X2Icon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ComponentType, SVGProps, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/AuthContext";
import useUserDetails from "../../hooks/useUserDetails";
import { Logo } from "../shared/brand";
import { SubmitButton } from "../shared/buttons/submit-button";
import Typography from "../shared/typography";
import { cn, formatPhoneNumberToHumanReadable } from "../utls/utils";

interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  match?: (pathname: string) => boolean;
}

interface AppSidebarProps {
  isMobileOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

function useNavItems(medicalRecordsPath: string, isAdmin: boolean) {
  return useMemo(() => {
    const main: NavItem[] = [
      {
        label: "Home",
        to: "/",
        icon: HomeModernIcon,
        match: (path) => path === "/",
      },
      {
        label: "Chat",
        to: "/chat",
        icon: ChatBubbleLeftRightIcon,
        match: (path) => path.startsWith("/chat"),
      },
      {
        label: "Group Chats",
        to: "/group-chats",
        icon: UserGroupIcon,
        match: (path) =>
          path.startsWith("/group-chats") || path.startsWith("/group-chat"),
      },
      {
        label: "Contacts",
        to: "/contacts",
        icon: IdentificationIcon,
        match: (path) => path.startsWith("/contacts"),
      },
      {
        label: "Medical Records",
        to: medicalRecordsPath,
        icon: ClipboardDocumentListIcon,
        match: (path) => path.startsWith("/medical-records"),
      },
      {
        label: "Content",
        to: "/content",
        icon: BookOpenIcon,
        match: (path) => path.startsWith("/content"),
      },
      {
        label: "Files",
        to: "/files",
        icon: FolderIcon,
        match: (path) => path.startsWith("/files"),
      },
    ];

    const admin: NavItem[] = isAdmin
      ? [
          {
            label: "All Chats",
            to: "/admin/all-chats",
            icon: QueueListIcon,
            match: (path) => path.startsWith("/admin/all-chats"),
          },
          {
            label: "Inbound Jobs",
            to: "/admin/inbound-document-jobs",
            icon: InboxArrowDownIcon,
            match: (path) => path.startsWith("/admin/inbound-document-jobs"),
          },
          {
            label: "Medication Harness",
            to: "/admin/dev-tools/medication-order-harness",
            icon: BeakerIcon,
            match: (path) =>
              path.startsWith("/admin/dev-tools/medication-order-harness"),
          },
          {
            label: "Admin Content",
            to: "/admin/content?tab=media#media",
            icon: FolderIcon,
            match: (path) =>
              path.startsWith("/admin/content"),
          },
          {
            label: "Assistant Voice",
            to: "/admin/assistant-voice",
            icon: PhoneIcon,
            match: (path) => path.startsWith("/admin/assistant-voice"),
          },
          {
            label: "Message Templates",
            to: "/admin/templates",
            icon: DocumentTextIcon,
            match: (path) =>
              path.startsWith("/admin/templates") ||
              path.startsWith("/admin/message-templates"),
          },
          {
            label: "Calls",
            to: "/admin/calls",
            icon: PhoneIcon,
            match: (path) => path.startsWith("/admin/calls"),
          },
          {
            label: "MR Requests",
            to: "/medical-records/requests/all",
            icon: ClipboardDocumentListIcon,
            match: (path) => path.startsWith("/medical-records/requests/all"),
          },
          {
            label: "Theme",
            to: "/theme",
            icon: Squares2X2Icon,
            match: (path) => path.startsWith("/theme"),
          },
        ]
      : [];

    return { main, admin };
  }, [medicalRecordsPath, isAdmin]);
}

export function AppSidebar({
  isMobileOpen,
  onClose,
  isAdmin,
}: AppSidebarProps) {
  const { user, isAuthenticated, signOut } = useAuth();
  const navigate = useNavigate();
  const { userDetails } = useUserDetails();
  const location = useLocation();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const medicalRecordsPath = "/medical-records";
  const computedIsAdmin =
    typeof isAdmin === "boolean" ? isAdmin : userDetails?.type === "admin";

  const { main: mainNavItems, admin: adminNavItems } = useNavItems(
    medicalRecordsPath,
    computedIsAdmin
  );

  if (!isAuthenticated) return null;

  const formattedPhone = user?.phone
    ? formatPhoneNumberToHumanReadable(user.phone)
    : undefined;

  const userLabel = userDetails?.first_name
    ? `${userDetails.first_name} ${userDetails.last_name ?? ""}`.trim()
    : user?.email ?? formattedPhone ?? "";

  const userSubLabel = user?.email ?? formattedPhone;

  const isActive = (item: NavItem) => {
    if (item.match) {
      return item.match(location.pathname);
    }
    return location.pathname === item.to;
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    await signOut();
    navigate("/login", { replace: true });
    setIsSigningOut(false);
    onClose();
  };

  const navContent = (
    <div className="flex h-full flex-col bg-cream-50">
      <div className="flex items-center gap-3 border-b border-cream-200 px-6 py-5">
        <Logo size="sm" textClassName="text-terracotta-600" />
      </div>
      <div className="flex-1 space-y-6 overflow-y-auto px-4 py-6">
        <div>
          <Typography
            variant="caption"
            className="mb-2 block text-xs uppercase tracking-wide text-brown-400"
          >
            Main
          </Typography>
          <nav className="space-y-2">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-4 py-2 text-sm font-medium transition-all",
                    active
                      ? "bg-terracotta-100 text-terracotta-900 shadow-sm"
                      : "text-brown-600 hover:bg-cream-200 hover:text-brown-900"
                  )}
                  onClick={onClose}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {adminNavItems.length > 0 && (
          <div>
            <Typography
              variant="caption"
              className="mb-2 mt-6 block text-xs uppercase tracking-wide text-brown-400"
            >
              Admin
            </Typography>
            <nav className="space-y-2">
              {adminNavItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={cn(
                      "flex items-center gap-3 rounded-xl px-4 py-2 text-sm font-medium transition-all",
                      active
                        ? "bg-terracotta-100 text-terracotta-900 shadow-sm"
                        : "text-brown-600 hover:bg-cream-200 hover:text-brown-900"
                    )}
                    onClick={onClose}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </div>
      <div className="space-y-3 border-t border-cream-200 px-6 py-5">
        <div>
          <Typography variant="body2" className="font-medium text-brown-800">
            {userLabel}
          </Typography>
          {userSubLabel && (
            <Typography variant="caption" className="text-brown-500">
              {userSubLabel}
            </Typography>
          )}
        </div>
        <Link
          to="/account"
          className="block rounded-lg border border-transparent px-3 py-2 text-sm font-medium text-brown-700 transition-colors hover:border-terracotta-200 hover:text-terracotta-700"
          onClick={onClose}
        >
          Account Settings
        </Link>
        <SubmitButton
          onClick={handleSignOut}
          iconOnly={false}
          color="default"
          size="sm"
          className="w-full border-terracotta-100 bg-terracotta-100 text-terracotta-700 hover:bg-terracotta-200"
          isDisabled={isSigningOut}
        >
          {isSigningOut ? "Signing Out..." : "Sign Out"}
        </SubmitButton>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:flex">
        <div className="fixed inset-y-0 left-0 z-40 w-72 border-r border-cream-200 shadow-sm">
          {navContent}
        </div>
      </aside>
      <div
        className={cn(
          "fixed inset-0 z-50 flex lg:hidden",
          isMobileOpen ? "pointer-events-auto" : "pointer-events-none"
        )}
      >
        <div
          className={cn(
            "absolute inset-0 bg-black/40 transition-opacity",
            isMobileOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={onClose}
        />
        <div
          className={cn(
            "relative h-full w-72 max-w-sm transform border-r border-cream-200 bg-cream-50 shadow-xl transition-transform",
            isMobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <button
            type="button"
            className="absolute right-4 top-4 rounded-full p-1 text-brown-500 hover:bg-cream-200"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          {navContent}
        </div>
      </div>
    </>
  );
}

export default AppSidebar;
